import React, { createContext, useEffect, useReducer } from 'react';
import jwtDecode from 'jwt-decode';
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';
import axiosServices from 'utils/axios-services';
import { InitialLoginContextProps, JWTContextType } from 'types/auth';

const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

type JWTPayload = {
    exp: number;
    [key: string]: any;
};

const verifyToken = (serviceToken: string | null | undefined) => {
    if (!serviceToken) return false;
    const decoded = jwtDecode<JWTPayload>(serviceToken);
    return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken: string | null | undefined) => {
    if (serviceToken) {
        localStorage.setItem('serviceToken', serviceToken);
        axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    } else {
        localStorage.removeItem('serviceToken');
        delete axios.defaults.headers.common.Authorization;
    }
};

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactNode }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = localStorage.getItem('serviceToken');
                if (serviceToken && verifyToken(serviceToken)) {
                    setSession(serviceToken);
                    const response = await axiosServices.get('/me');
                    const requires_2fa = response.data.requires_2fa ?? false;
                    const setup_required = response.data.setup_required ?? false;
                    const temp_token = response.data.temp_token;
                    const qr_code = response.data.qr_code;

                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: !(requires_2fa || setup_required),
                            user: {
                                ...response.data.user,
                                tempToken: temp_token,
                                requires2FA: requires_2fa || setup_required,
                                setupRequired: setup_required,
                                qrCode: qr_code ?? null
                            }
                        }
                    });
                } else {
                    localStorage.removeItem('serviceToken');
                    const tempToken = localStorage.getItem('tempToken');
                    if (tempToken) {
                        const res = await axiosServices.get('/auth/check-temp-token', {
                            headers: { Authorization: `Bearer ${tempToken}` }
                        });

                        if (res.data?.status === 'ok') {
                            dispatch({
                                type: LOGIN,
                                payload: {
                                    isLoggedIn: false,
                                    user: {
                                        ...res.data.user,
                                        tempToken,
                                        requires2FA: true,
                                        setupRequired: res.data.setup_required ?? true,
                                        qrCode: res.data.qr_code ?? null
                                    }
                                }
                            });
                        } else {
                            localStorage.removeItem('tempToken');
                            localStorage.removeItem('pending2FA');
                            dispatch({ type: LOGOUT });
                        }
                    } else {
                        dispatch({ type: LOGOUT });
                    }
                }
            } catch {
                localStorage.removeItem('tempToken');
                localStorage.removeItem('pending2FA');
                localStorage.removeItem('serviceToken');
                dispatch({ type: LOGOUT });
            }
        };

        init();
    }, []);

    const login = async (email: string, password: string) => {
        const response = await axiosServices.post('/login', { email, password });
        const requires_2fa = response.data.requires_2fa ?? false;
        const setup_required = response.data.setup_required ?? false;
        const temp_token = response.data.temp_token;
        const qr_code = response.data.qr_code;
        const data = response.data;

        // CASE 1: Full login (token exists) → admin or already verified
        if (data.token) {
            const { token, user: verifiedUser } = data;

            // Store token and user
            setSession(token);
            localStorage.removeItem('tempToken');
            localStorage.setItem('users', JSON.stringify(verifiedUser));

            dispatch({
                type: LOGIN,
                payload: {
                    isLoggedIn: true,
                    user: verifiedUser
                }
            });

            return;
        }
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: !(requires_2fa || setup_required),
                user: {
                    email,
                    tempToken: temp_token,
                    requires2FA: requires_2fa || setup_required,
                    setupRequired: setup_required ?? false,
                    qrCode: qr_code ?? null
                }
            }
        });

        localStorage.setItem('tempToken', temp_token);
        localStorage.setItem('pending2FA', JSON.stringify({ email }));
    };

    const register = async (
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        country: string,
        phone?: string,
        confirmPassword?: string
    ) => {
        await axiosServices.post('/register', {
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            country,
            phone,
            confirmPassword
        });
    };
    const resetPassword = async (email: string) => {
        await axiosServices.post('/reset-password', { email });
    };
    const updateProfile = () => {};
    const logout = () => {
        setSession(null);
        localStorage.removeItem('tempToken');
        localStorage.removeItem('serviceToken');
        dispatch({ type: LOGOUT });
    };
    const forgotPassword = async (email: string) => {
        // This will request a reset link or OTP to be sent
        await axiosServices.post('/forget-password', { email });
    };
    const enable2fa = async () => {
        const response = await axiosServices.post('/enable-2fa');
        return response.data;
    };

    const verify2fa = async (code: string) => {
        const tempToken = state.user?.tempToken;
        const response = await axiosServices.post(
            '/verify-2fa',
            { code },
            {
                headers: {
                    Authorization: `Bearer ${tempToken}`
                }
            }
        );

        const { token, user: verifiedUser } = response.data;
        setSession(token);
        localStorage.removeItem('tempToken');

        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                isInitialized: true,
                user: verifiedUser
            }
        });
    };

    if (!state.isInitialized) return <Loader />;

    return (
        <JWTContext.Provider
            value={{
                ...state,
                login,
                logout,
                register,
                enable2fa,
                verify2fa,
                forgotPassword,
                resetPassword,
                updateProfile
            }}
        >
            {children}
        </JWTContext.Provider>
    );
};

export default JWTContext;
